import React, { useState } from "react";
import { Mail, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SITE } from "@/lib/config";
import PrivacyClause from "@/components/legal/PrivacyClause";
import PrivacyCheckbox from "@/components/legal/PrivacyCheckbox";
import SEO, { breadcrumbSchema } from "@/components/seo/SEO";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [status, setStatus] = useState({ type: null, msg: "" });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus({ type: null, msg: "" });
        try {
            await api.post("/contact", form);
            setStatus({
                type: "success",
                msg: "Gracias, hemos recibido tu mensaje. Te responderemos lo antes posible.",
            });
            setForm(initial);
            setPrivacyAccepted(false);
        } catch (err) {
            setStatus({
                type: "error",
                msg: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-white">
            <SEO
                title="Contacto"
                description="Contacta con ASATTA, la Asociación de Alojamientos Turísticos de Tarifa. Escríbenos a info@asatta.org o utiliza el formulario."
                path="/contacto"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "ContactPage",
                        "url": "https://asatta.org/contacto",
                        "name": "Contacto · ASATTA",
                    },
                    breadcrumbSchema([
                        { name: "Inicio", path: "/" },
                        { name: "Contacto", path: "/contacto" },
                    ]),
                ]}
            />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                            Contacto
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-semibold text-[#082050] tracking-tight">
                            Hablemos.
                        </h1>
                        <p className="mt-4 text-[#4A5568] text-lg">
                            ¿Tienes dudas sobre asociarte o quieres colaborar con ASATTA?
                            Escríbenos y te responderemos en breve.
                        </p>

                        <div className="mt-10 space-y-6 text-[#4A5568]">
                            <div className="flex items-start gap-3">
                                <MapPin size={18} className="text-[#082050] mt-0.5" />
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-[#082050]/60">Dirección</div>
                                    <div>{SITE.address}</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Mail size={18} className="text-[#082050] mt-0.5" />
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-[#082050]/60">Email</div>
                                    <a href={`mailto:${SITE.email}`} className="hover:text-[#082050]">
                                        {SITE.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            className="border border-[#E2E4E9] p-6 sm:p-10 space-y-5 bg-white"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#082050]/70">Nombre</label>
                                    <input
                                        name="name"
                                        required
                                        value={form.name}
                                        onChange={onChange}
                                        data-testid="contact-name"
                                        className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#082050]/70">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="contact-email"
                                        className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#082050]/70">Teléfono</label>
                                    <input
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        data-testid="contact-phone"
                                        className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-[#082050]/70">Asunto</label>
                                    <input
                                        name="subject"
                                        value={form.subject}
                                        onChange={onChange}
                                        data-testid="contact-subject"
                                        className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050]"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-[#082050]/70">Mensaje</label>
                                <textarea
                                    name="message"
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={onChange}
                                    data-testid="contact-message"
                                    className="mt-2 w-full border border-[#E2E4E9] px-4 py-3 text-sm focus:outline-none focus:border-[#082050] resize-y"
                                />
                            </div>

                            {status.msg && (
                                <div
                                    data-testid={status.type === "success" ? "contact-success" : "contact-error"}
                                    className={`text-sm border px-4 py-3 ${
                                        status.type === "success"
                                            ? "border-[#082050] text-[#082050] bg-[#082050]/5"
                                            : "border-red-500 text-red-600 bg-red-50"
                                    }`}
                                >
                                    {status.msg}
                                </div>
                            )}

                            <PrivacyClause variant="contact" />
                            <PrivacyCheckbox
                                checked={privacyAccepted}
                                onChange={setPrivacyAccepted}
                                testId="contact-privacy-checkbox"
                            />

                            <button
                                type="submit"
                                disabled={submitting || !privacyAccepted}
                                data-testid="btn-contact-submit"
                                className="bg-[#082050] text-white px-8 py-4 text-sm font-medium hover:bg-[#082050]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:-translate-y-0.5"
                            >
                                {submitting ? "Enviando..." : "Enviar mensaje"}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
